import React, { FC, useEffect, useState } from 'react';
import './vendor/cadesplugin_api';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { IntegrationInstructions } from '@mui/icons-material';
import Box from '@mui/material/Box';
import ESignProvider from './ESignProvider';
import useAccount from '../../hooks/useAccount';
import { AccountType } from '../../enum/AccountType';
import { EsignProviderCerificateResponse } from '../../structures/response/ESignProviderCertificateResponse';
import uniqueByKey from '../../helpers/uniqueByKey';
import MfAlert from '../UI/Alert/MfAlert';
import { FileManaged } from '../../structures/response/FileManaged';
import MfButton from '../UI/Button/MfButton';
import { fileView } from '../../api/file';
import useClosableSnackbar from '../../hooks/useClosableSnackbar';
import { errorToString } from '../../helpers/Helper';
import MfLinearSimpleProgress from '../UI/Progress/MfLinearSimpleProgress';

const eSignProvider = new ESignProvider({});

interface Props {
  allowedInn?: string | string[];
  documents: FileManaged[];
  onSignCompleted: (signatures: { [key: string]: string }) => void;
}

const ESignComponent: FC<Props> = ({
  allowedInn = '',
  documents,
  onSignCompleted,
}) => {
  const { showSnackbar } = useClosableSnackbar();
  const [processing, setProcessing] = useState(true);
  const [selectedCertificate, setSelectedCertificate] =
    React.useState<EsignProviderCerificateResponse | null>(null);
  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    nextView: EsignProviderCerificateResponse
  ) => {
    setSelectedCertificate(nextView);
  };
  const { accountResponse } = useAccount();
  const [certificates, setCertificates] = useState<
    EsignProviderCerificateResponse[]
  >([]);

  const hasDocuments = Array.isArray(documents) && documents.length > 0;

  const certificatesToDisplay = uniqueByKey<EsignProviderCerificateResponse>(
    certificates,
    'fingerprint'
  ).sort((a, b) => +a.notBefore - +b.notBefore);

  const hasCertificates =
    Array.isArray(certificatesToDisplay) && certificatesToDisplay.length > 0;

  const canSign = hasDocuments && Boolean(selectedCertificate?.fingerprint);

  useEffect(() => {
    if (accountResponse) {
      init();
    }
  }, [accountResponse]);

  if (!accountResponse) {
    return null;
  }

  const init = async () => {
    setProcessing(true);
    await eSignProvider.init();
    await eSignProvider.updateCertificates();
    const providerCerts: EsignProviderCerificateResponse[] =
      eSignProvider.certificates.map(
        (certificate: EsignProviderCerificateResponse) => {
          const inn =
            accountResponse?.type === AccountType.LEGAL
              ? accountResponse?.profile.inn
              : accountResponse?.person.inn;

          let inns = ['6163209391'];
          if (Array.isArray(allowedInn) && allowedInn.length > 0) {
            inns = [...inns, ...allowedInn];
          } else if (inn) {
            inns.push(inn);
          }

          const disableByInn = !inns.some((v) =>
            certificate.subject.includes(v)
          );

          const exceptedUserId: Array<string | number> = [
            // 13008
          ];
          const isUserExcepted = exceptedUserId.includes(accountResponse.id);

          return {
            ...certificate,
            disabled: isUserExcepted ? false : disableByInn,
          };
        }
      );
    setCertificates(providerCerts);
    setProcessing(false);
  };

  const handleSignDocuments = async () => {
    if (eSignProvider?.currentProvider && hasDocuments) {
      setProcessing(true);

      try {
        const getFileContentPromises = documents.map((d: FileManaged) =>
          fileView(d.id, true)
        );
        const files: FileManaged[] = await Promise.all(getFileContentPromises);
        const signaturePromises = files.map(({ content }: FileManaged) =>
          eSignProvider.signData(
            content,
            selectedCertificate?.fingerprint,
            true
          )
        );
        const signatures = await Promise.all(signaturePromises);
        const result = signatures.reduce(
          (acc, s, i) => ({ ...acc, [files[i].id]: s }),
          {}
        );
        onSignCompleted(result);
      } catch (e) {
        showSnackbar(errorToString(e));
        console.log(e);
      }
      // this.setState({ signingInProgress: false });
    }
  };

  if (processing) {
    return <MfLinearSimpleProgress />;
  }

  if (!processing && !eSignProvider.currentProvider) {
    return (
      <MfAlert>
        <div>
          <div>
            К сожалению, на Вашем компьютере не установлено необходимое
            программное обеспечение. Чтобы получить больше информации, перейдите
            в раздел &nbsp;
            <a target="_blank" href="#/app/faq/14">
              «Вопросы и ответы»
            </a>
          </div>
        </div>
      </MfAlert>
    );
  }

  return (
    <div>
      <h4>Выберите сертификат ЭЦП для подписания</h4>
      <ToggleButtonGroup
        orientation="vertical"
        value={selectedCertificate}
        exclusive
        onChange={handleChange}
        color="primary"
      >
        {certificatesToDisplay.map((v) => (
          <ToggleButton key={v.fingerprint} value={v} aria-label="list">
            <IntegrationInstructions />{' '}
            <Box
              sx={{ ml: 3 }}
              style={{ textAlign: 'left', fontSize: '12px', lineHeight: 1.2 }}
            >
              {v.subject}
            </Box>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {!hasCertificates && <span>Список сертификатов пуст</span>}
      <Box sx={{ textAlign: 'center' }}>
        <MfButton
          sx={{ mt: 3 }}
          disabled={processing || !canSign}
          onClick={handleSignDocuments}
          variant="contained"
        >
          Подписать КЭП
        </MfButton>
      </Box>
    </div>
  );
};

export default ESignComponent;
